import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  usePayrollDeleteMutation,
  usePayrollListQuery,
  usePayrollStatusMutation,
  usePayrollSummaryQuery,
} from "../api/queries";
import { PayrollFilter, type PayrollListSearchParams } from "../ui/PayrollFilter";
import { PayrollSummaryCards } from "../ui/PayrollSummaryCards";
import { PayrollTable } from "../ui/PayrollTable";
import { PayrollListTour, usePayrollListTour } from "../tour/PayrollListTour";
import { Button, Modal, toast } from "@devhop/ui";
import { useEffect, useState } from "react";
import { HelpCircle, PlusIcon } from "lucide-react";

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

export function PayrollListPage() {
  const nav = useNavigate({ from: "/app/hr/payroll" });
  const search = useSearch({ from: "/app/hr/payroll" }) as PayrollListSearchParams;

  const periodYear = search.periodYear ?? currentYear;
  const periodMonth = search.periodMonth ?? currentMonth;
  const [yearInput, setYearInput] = useState(String(periodYear));
  const [monthInput, setMonthInput] = useState(String(periodMonth));

  useEffect(() => {
    setYearInput(String(periodYear));
    setMonthInput(String(periodMonth));
  }, [periodYear, periodMonth]);

  const listQuery = usePayrollListQuery({
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sort: search.sort,
    filters: search.filters,
    periodYear,
    periodMonth,
  });

  const summaryQuery = usePayrollSummaryQuery({
    periodYear,
    periodMonth,
    filters: search.filters,
  });

  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const statusMutation = usePayrollStatusMutation();
  const deleteMutation = usePayrollDeleteMutation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteModal, setDeleteModal] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [bulkModal, setBulkModal] = useState<{
    action: "confirm" | "paid" | "unconfirm";
    ids: string[];
  } | null>(null);
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[] | null>(null);

  const handleConfirm = (row: { id: string }) => {
    toast.promise(
      statusMutation.mutateAsync({ id: row.id, status: "confirmed" }),
      {
        loading: "ກຳລັງຢືນຢັນ...",
        success: "ຢືນຢັນເງິນເດືອນແລ້ວ",
        error: "ຢືນຢັນບໍ່ສຳເລັດ",
      },
    );
  };

  const handleMarkPaid = (row: { id: string }) => {
    toast.promise(
      statusMutation.mutateAsync({ id: row.id, status: "paid" }),
      {
        loading: "ກຳລັງບັນທຶກສະຖານະຊຳລະ...",
        success: "ບັນທຶກສະຖານະຊຳລະແລ້ວ",
        error: "ອັບເດດສະຖານະບໍ່ສຳເລັດ",
      },
    );
  };

  const handleUnconfirm = (row: { id: string }) => {
    toast.promise(
      statusMutation.mutateAsync({ id: row.id, status: "draft" }),
      {
        loading: "ກຳລັງປົດຢືນຢັນ...",
        success: "ປົດຢືນຢັນແລ້ວ (ກັບໄປສະຖານະຮ່າງ)",
        error: "ປົດຢືນຢັນບໍ່ສຳເລັດ",
      },
    );
  };

  const idsForConfirm = (ids: string[]) =>
    data
      .filter(
        (r) =>
          ids.includes(r.id) &&
          (r.status === "draft" || r.status === "confirmed"),
      )
      .map((r) => r.id);
  const idsForMarkPaid = (ids: string[]) =>
    data
      .filter(
        (r) =>
          ids.includes(r.id) &&
          (r.status === "confirmed" || r.status === "paid"),
      )
      .map((r) => r.id);
  const idsForUnconfirm = (ids: string[]) =>
    data
      .filter((r) => ids.includes(r.id) && r.status === "confirmed")
      .map((r) => r.id);

  const runBulkConfirm = (ids: string[]) => {
    const targetIds = idsForConfirm(ids);
    if (targetIds.length === 0) return;
    const promise = Promise.all(
      targetIds.map((id) =>
        statusMutation.mutateAsync({ id, status: "confirmed" }),
      ),
    );
    toast.promise(promise, {
      loading: "ກຳລັງຢືນຢັນທີ່ເລືອກ...",
      success: "ຢືນຢັນເງິນເດືອນແລ້ວ",
      error: "ຢືນຢັນບໍ່ສຳເລັດ",
    });
    void promise
      .then(() => {
        setSelectedIds([]);
        setBulkModal(null);
        listQuery.refetch();
      })
      .catch(() => setBulkModal(null));
  };

  const runBulkMarkPaid = (ids: string[]) => {
    const targetIds = idsForMarkPaid(ids);
    if (targetIds.length === 0) return;
    const promise = Promise.all(
      targetIds.map((id) =>
        statusMutation.mutateAsync({ id, status: "paid" }),
      ),
    );
    toast.promise(promise, {
      loading: "ກຳລັງບັນທຶກສະຖານະຊຳລະທີ່ເລືອກ...",
      success: "ບັນທຶກສະຖານະຊຳລະແລ້ວ",
      error: "ອັບເດດສະຖານະບໍ່ສຳເລັດ",
    });
    void promise
      .then(() => {
        setSelectedIds([]);
        setBulkModal(null);
        listQuery.refetch();
      })
      .catch(() => setBulkModal(null));
  };

  const runBulkUnconfirm = (ids: string[]) => {
    const targetIds = idsForUnconfirm(ids);
    if (targetIds.length === 0) return;
    const promise = Promise.all(
      targetIds.map((id) =>
        statusMutation.mutateAsync({ id, status: "draft" }),
      ),
    );
    toast.promise(promise, {
      loading: "ກຳລັງປົດຢືນຢັນທີ່ເລືອກ...",
      success: "ປົດຢືນຢັນແລ້ວ",
      error: "ປົດຢືນຢັນບໍ່ສຳເລັດ",
    });
    void promise
      .then(() => {
        setSelectedIds([]);
        setBulkModal(null);
        listQuery.refetch();
      })
      .catch(() => setBulkModal(null));
  };

  const handleBulkConfirm = (ids: string[]) =>
    setBulkModal({ action: "confirm", ids });
  const handleBulkMarkPaid = (ids: string[]) =>
    setBulkModal({ action: "paid", ids });
  const handleBulkUnconfirm = (ids: string[]) =>
    setBulkModal({ action: "unconfirm", ids });

  const draftIdsFromSelection = (ids: string[]) =>
    data
      .filter((r) => ids.includes(r.id) && r.status === "draft")
      .map((r) => r.id);

  const handleBulkDelete = (ids: string[]) => setBulkDeleteIds(ids);
  const runBulkDelete = () => {
    if (!bulkDeleteIds) return;
    const targetIds = draftIdsFromSelection(bulkDeleteIds);
    if (targetIds.length === 0) {
      setBulkDeleteIds(null);
      return;
    }
    const promise = Promise.all(
      targetIds.map((id) => deleteMutation.mutateAsync(id)),
    );
    toast.promise(promise, {
      loading: "ກຳລັງລຶບທີ່ເລືອກ...",
      success: "ລຶບເງິນເດືອນແລ້ວ",
      error: "ລຶບບໍ່ສຳເລັດ",
    });
    void promise
      .then(() => {
        setSelectedIds([]);
        setBulkDeleteIds(null);
        listQuery.refetch();
      })
      .catch(() => {
        setBulkDeleteIds(null);
      });
  };

  const handleDeleteClick = (row: { id: string; employeeName: string }) => {
    setDeleteModal({ id: row.id, name: row.employeeName });
  };
  const handleDeleteConfirm = () => {
    if (!deleteModal) return;
    const promise = deleteMutation.mutateAsync(deleteModal.id);
    toast.promise(promise, {
      loading: "ກຳລັງລຶບ...",
      success: "ລຶບເງິນເດືອນແລ້ວ",
      error: "ລຶບບໍ່ສຳເລັດ",
    });
    void promise
      .then(() => {
        setDeleteModal(null);
        listQuery.refetch();
      })
      .catch(() => {
        setDeleteModal(null);
      });
  };

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        filters: search.filters,
        periodYear,
        periodMonth,
      },
    });
  };

  const handleSortingChange = (id: string, desc: boolean) => {
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: [{ field: id, dir: desc ? "desc" : "asc" }],
        filters: search.filters,
        periodYear,
        periodMonth,
      },
    });
  };

  const handleHistory = () => {
    nav({
      to: "/app/hr/payroll/history",
      search: {
        offset: 0,
        limit: 20,
        periodYear: undefined,
        periodMonth: undefined,
        status: "paid",
        paidAtFrom: undefined,
        paidAtTo: undefined,
        search: undefined,
      },
    });
  };

  const handleViewPeriod = () => {
    const y = Number(yearInput) || currentYear;
    const m = Number(monthInput) || new Date().getMonth() + 1;
    nav({
      to: "/app/hr/payroll/period",
      search: {
        year: y,
        month: m,
        departmentId: undefined,
        status: undefined,
        search: undefined,
        sort: undefined,
      },
    });
  };

  const handleNewPayroll = () => {
    const y = Number(yearInput) || currentYear;
    const m = Number(monthInput) || new Date().getMonth() + 1;
    nav({
      to: "/app/hr/payroll/period/new",
      search: { year: y, month: m },
    });
  };

  const tour = usePayrollListTour();

  return (
    <>
      <Header />
      <Main>
        <PayrollListTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6" data-tourid="payroll-list-toolbar">
          <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-semibold text-2xl">ເງິນເດືອນ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ຈັດການເງິນເດືອນຕາມເດືອນ ແລະ ກວດສອບການຊຳລະພະນັກງານ.
              </p>
            </div>
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleViewPeriod}>
                ເບິ່ງຕາມເດືອນ
              </Button>
              <Button size="sm" onClick={handleNewPayroll}>
                <PlusIcon className="h-4 w-4" />
                ສ້າງເງິນເດືອນໃໝ່
              </Button>
            </div>
          </div>
        </div>

        <div data-tourid="payroll-list-summary">
          <PayrollSummaryCards
            data={summaryQuery.data}
            isLoading={summaryQuery.isLoading}
          />
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="payroll-list-filter"
        >
          <PayrollFilter />
          <div data-tourid="payroll-list-table">
            <PayrollTable
              data={data}
              isLoading={listQuery.isLoading}
              isError={!!listQuery.isError}
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              selectedIds={selectedIds}
              onSelectedIdsChange={setSelectedIds}
              onPaginationChange={handlePaginationChange}
              sortBy={search.sort?.[0]?.field}
              sortOrder={
                search.sort?.[0]?.dir as "asc" | "desc" | undefined
              }
              onSortingChange={handleSortingChange}
              onSlip={(row) =>
                nav({
                  to: "/app/hr/payroll/$id/slip",
                  params: { id: row.id },
                  search: { print: undefined },
                })
              }
              onEdit={(row) =>
                nav({
                  to: "/app/hr/payroll/$id/edit",
                  params: { id: row.id },
                })
              }
              onHistory={handleHistory}
              onConfirm={handleConfirm}
              onMarkPaid={handleMarkPaid}
              onUnconfirm={handleUnconfirm}
              onBulkConfirm={handleBulkConfirm}
              onBulkMarkPaid={handleBulkMarkPaid}
              onBulkUnconfirm={handleBulkUnconfirm}
              onBulkDelete={handleBulkDelete}
              onDelete={handleDeleteClick}
            />
          </div>
        </div>

        <Modal
          open={!!deleteModal}
          onOpenChange={(open) => !open && setDeleteModal(null)}
          title="ລຶບເງິນເດືອນ"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              ທ່ານຕ້ອງການລຶບເງິນເດືອນຂອງ {deleteModal?.name ?? "—"} ບໍ່?
              ການລຶບບໍ່ສາມາດກູ້ຄືນໄດ້.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteModal(null)}>
                ຍົກເລີກ
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm}>
                ຢືນຢັນລຶບ
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          open={!!bulkDeleteIds}
          onOpenChange={(open) => !open && setBulkDeleteIds(null)}
          title="ລຶບເງິນເດືອນທີ່ເລືອກ"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              ທ່ານຕ້ອງການລຶບເງິນເດືອນສະຖານະຮ່າງ{" "}
              {bulkDeleteIds ? draftIdsFromSelection(bulkDeleteIds).length : 0}{" "}
              ລາຍການບໍ່? ການລຶບບໍ່ສາມາດກູ້ຄືນໄດ້.
              (ພຽງແຕ່ລາຍການສະຖານະຮ່າງຈະຖືກລຶບ)
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkDeleteIds(null)}>
                ຍົກເລີກ
              </Button>
              <Button variant="destructive" onClick={runBulkDelete}>
                ລຶບ
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          open={!!bulkModal}
          onOpenChange={(open) => !open && setBulkModal(null)}
          title={
            bulkModal?.action === "confirm"
              ? "ຢືນຢັນເງິນເດືອນ"
              : bulkModal?.action === "unconfirm"
                ? "ປົດຢືນຢັນ"
                : "ບັນທຶກສະຖານະຊຳລະ"
          }
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              {bulkModal?.action === "confirm"
                ? `ທ່ານຕ້ອງການຢືນຢັນເງິນເດືອນ ${bulkModal?.ids.length ?? 0} ລາຍການບໍ່?`
                : bulkModal?.action === "unconfirm"
                  ? `ທ່ານຕ້ອງການປົດຢືນຢັນ (ກັບໄປສະຖານະຮ່າງ) ${bulkModal?.ids.length ?? 0} ລາຍການບໍ່?`
                  : `ທ່ານຕ້ອງການບັນທຶກສະຖານະຊຳລະ ${bulkModal?.ids.length ?? 0} ລາຍການບໍ່?`}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkModal(null)}>
                ຍົກເລີກ
              </Button>
              <Button
                onClick={() => {
                  if (!bulkModal) return;
                  if (bulkModal.action === "confirm")
                    runBulkConfirm(bulkModal.ids);
                  else if (bulkModal.action === "unconfirm")
                    runBulkUnconfirm(bulkModal.ids);
                  else runBulkMarkPaid(bulkModal.ids);
                }}
              >
                {bulkModal?.action === "confirm"
                  ? "ຢືນຢັນ"
                  : bulkModal?.action === "unconfirm"
                    ? "ປົດຢືນຢັນ"
                    : "ຊຳລະ"}
              </Button>
            </div>
          </div>
        </Modal>
      </Main>
    </>
  );
}